/**
 * Renderização de conteúdo de mensagem do chat interno.
 *
 * Saiu de dentro do MessageBubble.vue (1.002 linhas) porque é lógica pura:
 * entra texto, sai HTML. Fora de um componente ela vira testável de verdade
 * e passa a ser compartilhada por MessageRow.vue (layout novo em linha),
 * pela prévia da lista de conversas e pelo quote de resposta.
 *
 * REGRA DE SEGURANÇA: `escapeHtml` roda SEMPRE primeiro. Todo passo depois
 * dele opera sobre texto já escapado, então as únicas tags que chegam no
 * v-html são as que este arquivo gera. Nenhum passo novo pode ser inserido
 * antes do escape — é o que separa isto de um XSS.
 *
 * A ordem dos passos importa e não é arbitrária:
 *   1. escape        — neutraliza o que veio do usuário
 *   2. markdown-lite — consome `-`/`*` no início da linha antes do itálico
 *   3. menções       — consome `[@Nome](mention://user/ID/...)`
 *   4. links         — sobra só URL de verdade, sem `mention://` pra confundir
 *   5. highlight     — por último, pra poder marcar dentro do que já virou HTML
 */

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** @param {string} value @returns {string} */
export const escapeHtml = value =>
  String(value ?? '').replace(/[&<>"']/g, char => HTML_ESCAPES[char]);

/**
 * Marcador gravado pelo composer. Mesmo formato das notas privadas do inbox,
 * escolhido de propósito pra reaproveitar o parsing/notificação do backend.
 */
export const MENTION_RE = /\[@([^\]]+)\]\(mention:\/\/user\/(\d+)\/[^)]*\)/g;

/** Texto puro "@Nome" — pra prévia da sidebar e quote, que não usam v-html. */
export const stripMentionMarkup = text =>
  String(text ?? '').replace(MENTION_RE, (_full, name) => `@${name}`);

/**
 * Duas formas que o SERIALIZADOR do editor rico produz e que o markdown-lite
 * daqui não conhecia. Rodam antes de tudo, dos dois lados (bolha e prévia),
 * porque nenhum passo seguinte casaria com elas:
 *
 * - `texto\` + quebra de linha — é como o markdown escreve o Shift+Enter
 *   (`hard_break`). Sem tratar, aparecia uma contrabarra solta no fim da linha.
 * - `<https://x>` — autolink do ProseMirror quando se digita uma URL e um
 *   espaço. Sem tratar, os sinais de maior/menor escapados sobravam colados no
 *   link.
 *
 * Nada disso existia enquanto o campo era `<textarea>`: ali o que era digitado
 * era exatamente o que era gravado.
 */
const normalizarSaidaDoEditor = text =>
  String(text ?? '')
    .replace(/\\\n/g, '\n')
    .replace(/<(https?:\/\/[^\s>]+)>/g, '$1');

/** Remove os símbolos do markdown-lite sem converter pra HTML (prévias). */
export const stripMarkdownLite = text =>
  normalizarSaidaDoEditor(text)
    .replace(/```\n?([\s\S]*?)```/g, '$1')
    .replace(/^&gt; ?/gm, '')
    .replace(/^> ?/gm, '')
    .replace(/~~([^\n~]+)~~/g, '$1')
    .replace(/__([^\n_]+)__/g, '$1')
    .replace(/^[-*] +/gm, '• ')
    .replace(/`([^`\n]+)`/g, '$1')
    // A prévia mostra só o RÓTULO do link. Sem isto apareceria
    // `[contrato](https://...)` cru na sidebar — foi exatamente o bug de 2026-08-05
    // com `~~riscado~~`, quando esta função e a da bolha divergiram.
    .replace(/\[([^\]\n]+)\]\(https?:\/\/[^\s)]+\)/g, '$1')
    .replace(/\*\*([^\n*]+)\*\*/g, '$1')
    .replace(/(?<!\*)\*([^\n*]+)\*(?!\*)/g, '$1')
    .replace(/(?<![\w_])_([^\n_]+)_(?![\w_])/g, '$1');

/**
 * Referência a processo/cliente/caso/compromisso: `[jane](advup://cliente/5)`.
 *
 * Inserida pelos comandos com barra. Vira um link INTERNO — sem `href`, porque
 * navegar de verdade recarregaria o app inteiro e perderia o estado do chat.
 * Quem trata o clique é a bolha, via delegação, empurrando pelo router.
 *
 * Roda ANTES do markdown-lite consumir `[texto](url)`, senão o passo de link
 * genérico não casaria (só aceita http/https) e o marcador apareceria cru.
 *
 * O `id` é filtrado para dígitos e o rótulo já vem escapado do `escapeHtml`
 * anterior — este arquivo inteiro gera `v-html`, e um `data-` com aspas soltas
 * seria injeção de atributo.
 */
export const REFERENCIA_RE = /\[([^\]\n]+)\]\(advup:\/\/([a-z]+)\/(\d+)\)/g;

export const renderReferencias = escaped =>
  escaped.replace(
    REFERENCIA_RE,
    (_full, rotulo, tipo, id) =>
      `<a class="ic-ref" role="button" tabindex="0" data-ref-tipo="${tipo}" data-ref-id="${id}">${rotulo}</a>`
  );

/** Texto puro para prévia da sidebar e citação, que não usam v-html. */
export const stripReferencias = text =>
  String(text ?? '').replace(REFERENCIA_RE, (_full, rotulo) => rotulo);

const renderMentions = escaped =>
  escaped.replace(
    MENTION_RE,
    (_full, name) => `<span class="ic-mention">@${name}</span>`
  );

/**
 * A mesma normalização do `normalizarSaidaDoEditor`, só que sobre texto JÁ
 * escapado — onde `<` e `>` viraram `&lt;`/`&gt;`.
 *
 * Duplicada de propósito, e não movida para antes do escape: a regra do topo
 * deste arquivo é que `escapeHtml` roda primeiro e nada entra na frente dele.
 * Custa duas linhas; furar a regra custa a garantia inteira.
 */
const normalizarSaidaDoEditorEscapada = escaped =>
  escaped.replace(/\\\n/g, '\n').replace(/&lt;(https?:\/\/[^\s&]+)&gt;/g, '$1');

/**
 * Markdown-lite estilo Slack. A ordem aqui é frágil de propósito e está
 * documentada porque cada passo consome caracteres que o próximo usaria:
 *   ``` antes de `   — senão o code inline come a primeira crase do bloco
 *   lista antes de * — senão "* item" vira itálico
 *   ~~ antes de _    — não conflitam, mas mantém o par duplo junto
 */
const renderMarkdownLite = escapedBruto => {
  const escaped = normalizarSaidaDoEditorEscapada(escapedBruto);
  // O conteúdo do bloco de código tem que sair de cena ANTES dos outros
  // passos: senão a crase, o asterisco e o underscore de dentro dele são
  // interpretados como formatação. Guardo em placeholder e devolvo no fim.
  // (Pego escrevendo o teste: "```\na `b` c\n```" virava <code> dentro do
  // <pre>, ou seja, o bloco de código formatava o próprio conteúdo.)
  const blocks = [];
  const withPlaceholders = escaped.replace(
    /```\n?([\s\S]*?)```/g,
    (_full, code) => {
      blocks.push(code.trim());
      return `\uE000CODEBLOCK${blocks.length - 1}\uE001`;
    }
  );

  const formatted = withPlaceholders
    // citação: "> texto" no início da linha (o ">" já veio escapado)
    .replace(/^&gt; ?(.+)$/gm, '<span class="ic-quote">$1</span>')
    // lista: consome o marcador antes do itálico tentar casar o "*"
    .replace(/^[-*] +(.+)$/gm, '<span class="ic-li">• $1</span>')
    .replace(/^\d+\. +(.+)$/gm, '<span class="ic-li">$&</span>')
    .replace(/`([^`\n]+)`/g, '<code class="ic-code">$1</code>')
    .replace(/~~([^\n~]+)~~/g, '<s>$1</s>')
    // Sublinhado: `__texto__`. ANTES do negrito e do itálico — `__x__` também
    // casaria no `_x_` do itálico, e sairia um itálico dentro de outro em vez
    // de sublinhado.
    //
    // O Slack tem o botão mas não tem sintaxe de texto para sublinhado; aqui
    // precisa ter, porque o conteúdo é guardado como texto puro. `__` estava
    // livre: negrito é `**` e itálico é `_`.
    .replace(/__([^\n_]+)__/g, '<u>$1</u>')
    .replace(/\*\*([^\n*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*([^\n*]+)\*(?!\*)/g, '<em>$1</em>')
    .replace(/(?<![\w_])_([^\n_]+)_(?![\w_])/g, '<em>$1</em>')
    // Link com rótulo: `[texto](url)`. Roda DEPOIS das menções (que consomem
    // `[@Nome](mention://...)` antes) e ANTES do linkifyUrls, senão a URL de
    // dentro do parêntese seria linkada sozinha e sobraria `[texto](` solto.
    //
    // Só http/https: `javascript:` aqui seria XSS entregue de bandeja, já que
    // o conteúdo vira innerHTML.
    //
    // `data-url` alimenta o balãozinho de hover (CSS em `MessageRow.vue`): o
    // rótulo esconde o destino, e no Slack o destino aparece ao passar o mouse.
    .replace(
      /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="ic-link ic-rotulo" data-url="$2">$1</a>'
    );

  return formatted.replace(
    /\uE000CODEBLOCK(\d+)\uE001/g,
    (_full, index) =>
      `<pre class="ic-pre"><code>${blocks[Number(index)]}</code></pre>`
  );
};

const URL_RE = /(https?:\/\/[^\s<]+)/g;

/**
 * Aplica `troca` SÓ no texto visível, nunca dentro de uma tag.
 *
 * Sem isto o `linkifyUrls` casava a URL que o passo de markdown já tinha posto
 * dentro do `href` e devolvia âncora dentro de âncora — `[rótulo](url)` saía na
 * bolha como `https://x" target="_blank" …>rótulo`. Valia para TODA mensagem
 * com link de rótulo, inclusive as que já estão no banco.
 *
 * É a mesma técnica que o `highlightTerm` aqui embaixo já usava; agora as três
 * passadas que mexem em HTML pronto usam a mesma peça.
 */
const foraDasTags = (html, troca) =>
  html
    .split(/(<[^>]*>)/g)
    .map(pedaco => (pedaco.startsWith('<') ? pedaco : troca(pedaco)))
    .join('');

/**
 * Pontuação que fecha frase não faz parte da URL: "veja (https://x.com)."
 * tem que linkar `https://x.com` e deixar `).` de fora.
 */
const linkifyUrls = html =>
  foraDasTags(html, texto =>
    texto.replace(URL_RE, url => {
      const trailing = url.match(/[).,!?;:'"]+$/)?.[0] ?? '';
      const clean = trailing ? url.slice(0, -trailing.length) : url;
      if (!clean) return url;
      return `<a href="${clean}" target="_blank" rel="noopener noreferrer" class="ic-link">${clean}</a>${trailing}`;
    })
  );

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Numeração única do CNJ: NNNNNNN-DD.AAAA.J.TT.OOOO.
 * Mesmo formato validado em LegalProcess::CNJ_FORMAT.
 */
export const CNJ_RE = /\b\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}\b/g;

/**
 * Número de processo colado no chat vira link pra busca do módulo jurídico.
 *
 * Nasceu de uso real: na produção da A&T o Eduardo escreveu
 * "PROC INCIDENTAL, ex: Proc - 0000258/001" na mão. Eles já falam de processo
 * no chat — isto só torna clicável o que já está sendo digitado, sem exigir
 * que ninguém aprenda um comando.
 *
 * SEGURANÇA: aponta pra busca (`/processos?q=`), que o backend já escopa por
 * conta. Não confirma nem nega a existência do processo — se não for da conta,
 * a busca simplesmente não acha. Nenhum id é exposto aqui.
 */
const linkifyCnj = (html, accountId) => {
  if (!accountId) return html; // sem conta resolvida, degrada pra texto puro
  // Também fora das tags: um CNJ dentro de um `href` viraria âncora aninhada,
  // o mesmo defeito que o `linkifyUrls` tinha.
  return foraDasTags(html, texto =>
    texto.replace(CNJ_RE, cnj => {
      const href = `/app/accounts/${accountId}/processos?q=${encodeURIComponent(cnj)}`;
      return `<a href="${href}" class="ic-cnj" title="${cnj}">${cnj}</a>`;
    })
  );
};

/**
 * Marca o termo SÓ no texto visível, nunca dentro de uma tag.
 *
 * Antes o replace corria no HTML inteiro: buscar "processos" injetava <mark>
 * dentro do href e quebrava o link. Já valia pros links http; com o link de
 * CNJ (cujo href contém "/processos") passaria a acontecer sempre.
 */
const highlightTerm = (html, term) => {
  const query = String(term ?? '').trim();
  if (!query) return html;
  const pattern = new RegExp(escapeRegExp(escapeHtml(query)), 'gi');
  return foraDasTags(html, texto =>
    texto.replace(pattern, match => `<mark class="ic-mark">${match}</mark>`)
  );
};

/**
 * Pipeline completo: texto cru da mensagem → HTML seguro pro v-html.
 *
 * @param {string} content texto cru, como veio do banco
 * @param {{ highlightQuery?: string, accountId?: number|string }} [options]
 * @returns {string} HTML seguro
 */
export const renderMessageContent = (
  content,
  { highlightQuery = '', accountId = null } = {}
) =>
  highlightTerm(
    linkifyCnj(
      linkifyUrls(
        renderMentions(
          renderReferencias(renderMarkdownLite(escapeHtml(content)))
        )
      ),
      accountId
    ),
    highlightQuery
  );
